import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands';
import { InlineInputBoxAbortMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-abort-message-command';

import {
  type RovoContentBridgeAPI,
  createRovoBridgeAPIClient,
  type ClientId,
} from '@canvas/rovo-content-bridge-api';

import {
  BoeSubmitPromptCommand,
  type BoeSubmitPromptCommandProperties,
} from './commands/boe-submit-prompt.ts';
import {
  CreateNewRovoConvoCommand,
  type CreateNewRovoConvoCommandProperties,
} from './commands/create-new-rovo-convo.ts';
import {
  OpenRovoSidePanelCommand,
  type OpenRovoSidePanelCommandProperties,
} from './commands/open-rovo-side-panel-command.ts';
import {
  HidePaywallCommand,
  ShowPaywallCommand,
  type ShowPaywallCommandProperties,
} from './commands/paywall-commands.ts';
import {
  UpdatePanelStateCommand,
  type UpdatePanelStateCommandProperties,
} from './commands/update-panel-state.ts';
import { CONFLUENCE_AFM_CLIENT_ID, WHITEBOARDS_CLIENT_ID } from './types.ts';

interface AFMBridgeClientInput {
  clientId: ClientId;
  bridge: Pick<RovoContentBridgeAPI, 'send' | 'on' | 'off' | 'teardown'>;
}

/**
 * A simple wrapper class around the bridge client to try and keep the API simple
 */
class AFMBridgeClient {
  public constructor(private input: AFMBridgeClientInput) {
    // Ensure we unregister all command handlers when the page is unloaded to prevent
    // memory leaks when navigating
    const clientId = this.input.clientId;
    const cleanupBridge = () => {
      this.input.bridge.teardown(clientId);

      window.removeEventListener('beforeunload', cleanupBridge);
      window.removeEventListener('unload', cleanupBridge);
      window.removeEventListener('pagehide', cleanupBridge);
    };
    window.addEventListener('beforeunload', cleanupBridge, { once: true });
    window.addEventListener('unload', cleanupBridge, { once: true });
    window.addEventListener('pagehide', cleanupBridge, { once: true });
  }

  /**
   * Invokes a paywall. If the user is a site admin, they will see a different message than a regular user
   */
  public showPaywall(
    props: Pick<ShowPaywallCommandProperties, 'content' | 'featureId' | 'position'>,
  ) {
    // @ts-ignore - ReactElement in PaywallContent.body is not serializable according to CommandProperties index signature, but it works at runtime
    this.input.bridge.send(ShowPaywallCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      featureId: props.featureId,
      position: props.position,
      content: {
        siteAdmin: props.content.siteAdmin,
        default: props.content.default,
      },
    });
  }

  /**
   * When we get a paywall hide command, we invoke the provided callback
   * @param callback
   */
  public onPaywallClose(callback: () => void) {
    this.input.bridge.on(this.input.clientId, HidePaywallCommand, callback);
  }

  /** Create a new rovo conversation in AFM
   * @param prompt - The prompt for the new rovo conversation
   * @param contentId - The id of the content to use for the new rovo conversation
   */

  public createNewRovoConvo(
    props: Pick<CreateNewRovoConvoCommandProperties, 'prompt' | 'contentId'>,
  ) {
    this.input.bridge.send(CreateNewRovoConvoCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      prompt: props.prompt,
      contentId: props.contentId,
    });
  }

  /**
   * Submit a BOE prompt to AFM's Rovo chat using the existing content creation flow.
   * Unlike createNewRovoConvo, this sends the raw prompt without any wrapper text.
   */
  public boeSubmitPrompt(
    props: Pick<BoeSubmitPromptCommandProperties, 'prompt' | 'contentId'>,
  ) {
    this.input.bridge.send(BoeSubmitPromptCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      prompt: props.prompt,
      contentId: props.contentId,
    });
  }

  /**
   * Open the rovo side panel to a specific action or with a prefilled prompt.
   */
  public openRovoSidePanel(
    props: Pick<OpenRovoSidePanelCommandProperties, 'actionId' | 'prefillPrompt'>,
  ): void {
    this.input.bridge.send(OpenRovoSidePanelCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      actionId: props.actionId,
      prefillPrompt: props.prefillPrompt,
    });
  }

  /**
   * Abort the current Rovo LLM stream. AFM listens for this command on the Rovo chat
   * client and calls `abortRequests()` to cancel any in-flight LLM requests.
   * Use this when the user clicks a stop button mid-stream.
   */
  public abortStream(): void {
    this.input.bridge.send(InlineInputBoxAbortMessageCommand, {
      senderId: this.input.clientId,
      receiverId: ROVO_CHAT_CLIENT_ID,
    });
  }

  /**
   * Update the sidebar panel state in AFM
   * @param props - The panel state properties (panel and width)
   */
  public updatePanelState(
    props: Pick<UpdatePanelStateCommandProperties, 'panel' | 'width'>,
  ): void {
    this.input.bridge.send(UpdatePanelStateCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      panel: props.panel,
      width: props.width,
    });
  }
}

let afmBridgeClient: AFMBridgeClient;

/**
 * Get the singleton AFM bridge client instance
 */
export function getConfluenceAFMBridgeClient() {
  if (!afmBridgeClient) {
    afmBridgeClient = new AFMBridgeClient({
      clientId: WHITEBOARDS_CLIENT_ID,
      bridge: createRovoBridgeAPIClient(WHITEBOARDS_CLIENT_ID, () => {}),
    });
  }

  return afmBridgeClient;
}
